import { useMemo } from 'react';
import type {
  FrequencyStatsResponse,
  PairingsStatsResponse,
  GameId,
  GameDrawHistoryResponse,
  GameDrawResponse,
  GameInfoResponse,
  RulesCatalogResponse,
  RulesStatsResponse,
} from './types';
import { useApi } from './client';

export interface RulesStatsRequestRule {
  instanceId: string;
  kind?: string;
  id?: string;
  weight?: number;
  settings?: Record<string, string | number | boolean>;
}

export interface GameApi {
  getGameInfo: (gameId: GameId, recent?: number) => Promise<GameInfoResponse>;
  getPublicGameInfo: (gameId: GameId) => Promise<GameInfoResponse>;
  getGameDrawHistory: (gameId: GameId, page?: number, pageSize?: number) => Promise<GameDrawHistoryResponse>;
  getGameDraw: (gameId: GameId, drawDate: string) => Promise<GameDrawResponse>;
  postRulesStats: (gameId: GameId, rules: RulesStatsRequestRule[]) => Promise<RulesStatsResponse>;
  getRulesCatalog: () => Promise<RulesCatalogResponse>;
  getFrequencyStats: (gameId: GameId, window?: number) => Promise<FrequencyStatsResponse>;
  getPairingsStats: (gameId: GameId, window?: number) => Promise<PairingsStatsResponse>;
}

export function useGameApi(): GameApi {
  const api = useApi();
  return useMemo(
    () => ({
      getGameInfo: (gameId: GameId, recent = 20) =>
        api.get<GameInfoResponse>(`/v1/games/${gameId}?recent=${recent}`),
      getPublicGameInfo: (gameId: GameId) =>
        api.get<GameInfoResponse>(`/v1/public/games/${gameId}`),
      getGameDrawHistory: (gameId: GameId, page = 1, pageSize = 20) =>
        api.get<GameDrawHistoryResponse>(`/v1/games/${gameId}/draws?page=${page}&pageSize=${pageSize}`),
      getGameDraw: (gameId: GameId, drawDate: string) =>
        api.get<GameDrawResponse>(`/v1/games/${gameId}/draws/${encodeURIComponent(drawDate)}`),
      postRulesStats: (gameId: GameId, rules: RulesStatsRequestRule[]) =>
        api.post<RulesStatsResponse>(`/v1/games/${gameId}/rules/stats`, { rules }),
      getRulesCatalog: () => api.get<RulesCatalogResponse>('/v1/rules/catalog'),
      getFrequencyStats: (gameId: GameId, window = 100) =>
        api.get<FrequencyStatsResponse>(`/v1/games/${gameId}/stats/frequency?window=${window}`),
      getPairingsStats: (gameId: GameId, window = 100) =>
        api.get<PairingsStatsResponse>(`/v1/games/${gameId}/stats/pairings?window=${window}&topK=5&minLift=1.0`),
    }),
    [api]
  );
}
